/**
 * useDtc — React hook for DTC translation + attached DTC list management.
 *
 * Mirrors useAiLookup.ts. Manages loading/error state for each operation
 * independently.  Uses dtcService for HTTP calls.
 */

import { useState, useCallback } from 'react';
import {
  dtcService,
  DtcTranslateResult,
  DtcTranslateBody,
  AttachedDtc,
  DtcStatus,
  WorkOrderDiagnosis,
  DiagnosisBody,
} from '../services/dtcService';

export interface DtcState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** HTTP status when an error occurred — used to detect 402 / 503 */
  errorStatus: number | null;
}

function initialState<T>(): DtcState<T> {
  return { data: null, loading: false, error: null, errorStatus: null };
}

export interface UseDtcResult {
  /** Translation result (last translate call) */
  translation: DtcState<DtcTranslateResult>;
  /** Attached DTC list for the current work order */
  dtcList: DtcState<AttachedDtc[]>;
  /** C3 diagnoses list for the current work order */
  diagnoses: DtcState<WorkOrderDiagnosis[]>;

  translate: (body: DtcTranslateBody) => Promise<void>;
  attach: (woId: number, result: DtcTranslateResult) => Promise<void>;
  loadList: (woId: number) => Promise<void>;
  setStatus: (woId: number, id: string, status: DtcStatus) => Promise<void>;
  removeDtc: (woId: number, id: string) => Promise<void>;

  loadDiagnoses: (woId: number) => Promise<void>;
  createDiagnosis: (woId: number, body: DiagnosisBody) => Promise<void>;
  updateDiagnosis: (woId: number, id: string, body: DiagnosisBody) => Promise<void>;

  /** Reset the translation panel (e.g. when starting a new lookup) */
  clearTranslation: () => void;
}

/** Extract a human-readable message + HTTP status from an axios error */
function parseError(e: any): { msg: string; status: number | null } {
  const status: number | null = e?.response?.status ?? null;
  const raw =
    e?.response?.data?.error ??
    e?.response?.data?.message ??
    e?.message ??
    'Request failed';
  // The standardized API error envelope nests an object
  // ({ code, message, path, requestId }); coerce to a string so it can never
  // be rendered as a React child (which throws and white-screens the app).
  const msg: string =
    typeof raw === 'string' ? raw : raw?.message ?? raw?.error ?? 'Request failed';
  return { msg, status };
}

export function useDtc(): UseDtcResult {
  const [translation, setTranslation] = useState<DtcState<DtcTranslateResult>>(initialState());
  const [dtcList, setDtcList] = useState<DtcState<AttachedDtc[]>>(initialState());
  const [diagnoses, setDiagnoses] = useState<DtcState<WorkOrderDiagnosis[]>>(initialState());

  // ── Translation ─────────────────────────────────────────────────────────────

  const translate = useCallback(async (body: DtcTranslateBody) => {
    setTranslation({ data: null, loading: true, error: null, errorStatus: null });
    try {
      const result = await dtcService.translate(body);
      setTranslation({ data: result, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status } = parseError(e);
      setTranslation({ data: null, loading: false, error: msg, errorStatus: status });
    }
  }, []);

  const clearTranslation = useCallback(() => {
    setTranslation(initialState());
  }, []);

  // ── Attached DTC list ───────────────────────────────────────────────────────

  const loadList = useCallback(async (woId: number) => {
    setDtcList({ data: null, loading: true, error: null, errorStatus: null });
    try {
      const list = await dtcService.list(woId);
      setDtcList({ data: list, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status } = parseError(e);
      setDtcList({ data: null, loading: false, error: msg, errorStatus: status });
    }
  }, []);

  const attach = useCallback(async (woId: number, result: DtcTranslateResult) => {
    try {
      await dtcService.attach(woId, result);
      // Refresh the list after attaching
      const list = await dtcService.list(woId);
      setDtcList({ data: list, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status } = parseError(e);
      setDtcList((prev) => ({ ...prev, error: msg, errorStatus: status }));
    }
  }, []);

  const setStatus = useCallback(async (woId: number, id: string, status: DtcStatus) => {
    try {
      await dtcService.setStatus(woId, id, status);
      const list = await dtcService.list(woId);
      setDtcList({ data: list, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status: errStatus } = parseError(e);
      setDtcList((prev) => ({ ...prev, error: msg, errorStatus: errStatus }));
    }
  }, []);

  const removeDtc = useCallback(async (woId: number, id: string) => {
    try {
      await dtcService.remove(woId, id);
      const list = await dtcService.list(woId);
      setDtcList({ data: list, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status } = parseError(e);
      setDtcList((prev) => ({ ...prev, error: msg, errorStatus: status }));
    }
  }, []);

  // ── C3 diagnoses ────────────────────────────────────────────────────────────

  const loadDiagnoses = useCallback(async (woId: number) => {
    setDiagnoses({ data: null, loading: true, error: null, errorStatus: null });
    try {
      const list = await dtcService.listDiagnoses(woId);
      setDiagnoses({ data: list, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status } = parseError(e);
      setDiagnoses({ data: null, loading: false, error: msg, errorStatus: status });
    }
  }, []);

  const createDiagnosis = useCallback(async (woId: number, body: DiagnosisBody) => {
    try {
      await dtcService.createDiagnosis(woId, body);
      const list = await dtcService.listDiagnoses(woId);
      setDiagnoses({ data: list, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status } = parseError(e);
      setDiagnoses((prev) => ({ ...prev, error: msg, errorStatus: status }));
    }
  }, []);

  const updateDiagnosis = useCallback(async (woId: number, id: string, body: DiagnosisBody) => {
    try {
      await dtcService.updateDiagnosis(woId, id, body);
      const list = await dtcService.listDiagnoses(woId);
      setDiagnoses({ data: list, loading: false, error: null, errorStatus: null });
    } catch (e: any) {
      const { msg, status } = parseError(e);
      setDiagnoses((prev) => ({ ...prev, error: msg, errorStatus: status }));
    }
  }, []);

  return {
    translation,
    dtcList,
    diagnoses,
    translate,
    clearTranslation,
    attach,
    loadList,
    setStatus,
    removeDtc,
    loadDiagnoses,
    createDiagnosis,
    updateDiagnosis,
  };
}
